import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, PanelLeftClose, PanelLeft } from 'lucide-react';
import useGlobale from './global';

const Sidebar = ({ isOpen, menuItems, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useGlobale();
    const [collapsed, setCollapsed] = useState(false);
    
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1024 && isOpen) {
                onClose();
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen, onClose]);
    
    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm lg:hidden z-40"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed lg:sticky lg:top-0 inset-y-0 left-0 
                    ${collapsed ? 'w-[70px]' : 'w-[250px]'}
                    bg-white
                    shadow-xl
                    border-l-4 border-l-orange-500
                    transform transition-all duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    lg:translate-x-0 
                    z-50
                    flex flex-col
                    h-screen
                    overflow-hidden
                `}
            >
                {/* Mobile close button */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 lg:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Logo + Toggle */}
                <div className={`flex items-center ${collapsed ? 'flex-col py-3 px-2 space-y-2' : 'justify-between px-4 py-4'} border-b border-orange-100`}>
                    <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
                        <div className={`${collapsed ? 'w-10 h-10' : 'w-12 h-12'} rounded-full overflow-hidden border-2 border-orange-400 flex items-center justify-center bg-white transition-all duration-300 flex-shrink-0 shadow-sm`}>
                            <img 
                                src="/logo-anpe.png" 
                                alt="Logo ANPE" 
                                className="w-full h-full object-contain p-1"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                        {!collapsed && (
                            <div className="flex flex-col">
                                <h2 className="text-xl font-extrabold bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent tracking-tight">
                                    ANPE
                                </h2>
                                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-medium -mt-0.5">
                                    Niger
                                </span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className="hidden lg:flex p-1.5 rounded-md hover:bg-orange-50 text-orange-400 hover:text-orange-600 transition-colors"
                    >
                        {collapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-4 h-4" />}
                    </button>
                </div>

                {/* Profile */}
                <div className={`${collapsed ? 'px-2 py-3' : 'px-4 py-3'} border-b border-gray-100`}>
                    <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
                        <div className="relative flex-shrink-0">
                            <div className={`${collapsed ? 'w-9 h-9' : 'w-10 h-10'} rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm border-2 border-orange-200 transition-all`}>
                                {user?.first_name?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}{user?.last_name?.[0] || ''}
                            </div>
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>
                        {!collapsed && (
                            <div className="overflow-hidden">
                                <p className="text-[15px] font-semibold text-gray-800 truncate">{user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || 'Utilisateur'}</p>
                                <p className="text-xs text-gray-400">{user?.is_superuser ? 'Administrateur' : 'Demandeur'}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-3'} py-3 space-y-0.5 overflow-y-auto`}>
                    {menuItems.map((item, index) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <button
                                key={index}
                                onClick={() => {
                                    navigate(item.path);
                                    if (window.innerWidth < 1024) onClose();
                                }}
                                title={collapsed ? item.label : ''}
                                className={`
                                    flex items-center w-full
                                    ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'}
                                    text-[14px] font-medium rounded-lg
                                    transition-all duration-150
                                    ${isActive
                                        ? 'text-white bg-gradient-to-r from-orange-500 to-orange-400 shadow-sm shadow-orange-200'
                                        : 'text-gray-600 hover:bg-orange-50 hover:text-orange-700'
                                    }
                                `}
                            >
                                <div className={`flex-shrink-0 ${collapsed ? '' : 'mr-3'}`}>
                                    {React.cloneElement(item.icon, {
                                        className: `w-[18px] h-[18px] ${isActive ? 'text-white' : 'text-orange-400'}`
                                    })}
                                </div>
                                {!collapsed && <span className="truncate">{item.label}</span>}
                            </button>
                        );
                    })}
                </nav>

            </aside>
        </>
    );
};

export default Sidebar;